/* Mimi Marmo — "The Keepsake" cart behaviors
 *
 * The drawer's inner HTML is replaced wholesale by Dawn's cart JS on every
 * add / quantity change / removal, so nothing here binds to specific nodes:
 * document-level event delegation handles interactions, and a MutationObserver
 * on #CartDrawer re-runs idempotent init (the upsell rail) after each render.
 */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const mmDebounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const jsonConfig = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  /* ----------------------------------------------------------------------
     Gift note — saved to a cart attribute so it lands on the order
     ---------------------------------------------------------------------- */

  const saveGiftNote = mmDebounce((textarea) => {
    const status = textarea.closest('.mm-gift__card')?.querySelector('[data-mm-gift-status]');

    fetch(`${window.routes.cart_update_url}`, jsonConfig({ attributes: { 'Gift note': textarea.value.trim() } }))
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || cart.errors) throw new Error(cart && cart.errors);
        if (status) {
          status.textContent = textarea.value.trim() ? 'saved with your order' : '';
          status.style.opacity = '1';
          setTimeout(() => {
            status.style.opacity = '0';
          }, 2400);
        }
      })
      .catch(() => {
        if (status) {
          status.textContent = 'could not save — please retry';
          status.style.opacity = '1';
        }
      });
  }, 600);

  document.addEventListener('input', (event) => {
    const textarea = event.target.closest('[data-mm-gift-note]');
    if (textarea) saveGiftNote(textarea);
  });

  /* ----------------------------------------------------------------------
     Optimistic fade while a removal request is in flight
     ---------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('cart-remove-button');
    if (removeButton && removeButton.closest('#CartDrawer')) {
      removeButton.closest('.cart-item')?.classList.add('mm-citem--leaving');
    }
  });

  /* ----------------------------------------------------------------------
     "Complete the look" — recommendations with collection fallback
     ---------------------------------------------------------------------- */

  // cache resolved picks per (seed product, cart contents) so drawer
  // re-renders don't refetch or flicker
  const upsellCache = new Map();

  const toCents = (value) => {
    if (typeof value === 'number') return Math.round(value);
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) return null;
    return String(value).includes('.') ? Math.round(parsed * 100) : Math.round(parsed);
  };

  const formatMoney = (cents, currency) => {
    if (cents == null) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const sizedImage = (url, width) => {
    if (!url) return null;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  };

  // normalize a product from /recommendations/products.json or
  // /collections/{handle}/products.json into one shape
  const normalizeProduct = (product) => {
    const variants = product.variants || [];
    const availableVariants = variants.filter((variant) => variant.available !== false);
    if (product.available === false || availableVariants.length === 0) return null;

    const image =
      product.featured_image ||
      (product.images && product.images[0] && (product.images[0].src || product.images[0])) ||
      null;

    return {
      id: product.id,
      title: product.title,
      url: product.url ? product.url.split('?')[0] : `${ROOT}products/${product.handle}`,
      image: sizedImage(typeof image === 'string' ? image : null, 360),
      priceCents: toCents(availableVariants[0].price ?? product.price),
      // one-tap add only when there is nothing to choose
      variantId: variants.length === 1 ? availableVariants[0].id : null,
    };
  };

  // /collections/{handle}/products.json comes back in the collection's admin
  // order; re-sort to match the theme-wide default (Theme settings › Mimi
  // Marmo product order) so the fallback leads with the same pieces the
  // rest of the storefront does.
  const sortFallbackProducts = (products, sort) => {
    const list = [...(products || [])];
    const createdAt = (product) => Date.parse(product.created_at || product.published_at || 0) || 0;
    const price = (product) => {
      const variant = (product.variants || []).find((item) => item.available !== false) || (product.variants || [])[0];
      return toCents(variant ? variant.price : product.price) ?? Number.MAX_SAFE_INTEGER;
    };

    switch (sort) {
      case 'created-descending':
        return list.sort((a, b) => createdAt(b) - createdAt(a));
      case 'created-ascending':
        return list.sort((a, b) => createdAt(a) - createdAt(b));
      case 'price-ascending':
        return list.sort((a, b) => price(a) - price(b));
      case 'price-descending':
        return list.sort((a, b) => price(b) - price(a));
      case 'title-ascending':
        return list.sort((a, b) => String(a.title).localeCompare(String(b.title), undefined, { numeric: true }));
      default:
        return list;
    }
  };

  const fetchUpsellProducts = (seedProductId, inCartIds, fallbackHandle, fallbackSort) => {
    const excluded = new Set(inCartIds);
    const collect = (products) =>
      (products || [])
        .map(normalizeProduct)
        .filter((product) => product && !excluded.has(String(product.id)) && product.priceCents != null);

    return fetch(`${ROOT}recommendations/products.json?product_id=${seedProductId}&limit=6&intent=related`)
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .catch(() => ({ products: [] }))
      .then((data) => {
        const picks = collect(data.products);
        if (picks.length >= 2 || !fallbackHandle) return picks;

        return fetch(`${ROOT}collections/${fallbackHandle}/products.json?limit=50`)
          .then((response) => (response.ok ? response.json() : { products: [] }))
          .catch(() => ({ products: [] }))
          .then((fallbackData) => {
            const seen = new Set(picks.map((pick) => pick.id));
            for (const candidate of collect(sortFallbackProducts(fallbackData.products, fallbackSort))) {
              if (picks.length >= 2) break;
              if (!seen.has(candidate.id)) picks.push(candidate);
            }
            return picks;
          });
      });
  };

  const buildUpsellCard = (product, currency, comingSoonSrc) => {
    const card = document.createElement('div');
    card.className = 'mm-upsell-card';

    const media = document.createElement('a');
    media.className = 'mm-upsell-card__media';
    media.href = product.url;
    media.setAttribute('tabindex', '-1');
    media.setAttribute('aria-hidden', 'true');
    // pieces not yet photographed get the branded "coming soon" plate
    const imageSrc = product.image || comingSoonSrc;
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);

    const title = document.createElement('a');
    title.className = 'mm-upsell-card__title';
    title.href = product.url;
    title.textContent = product.title;
    card.appendChild(title);

    const price = document.createElement('span');
    price.className = 'mm-upsell-card__price';
    price.textContent = formatMoney(product.priceCents, currency);
    card.appendChild(price);

    if (product.variantId) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'mm-upsell-card__add';
      add.dataset.mmUpsellAdd = product.variantId;
      add.textContent = 'Add';
      add.setAttribute('aria-label', `Add ${product.title} to your selection`);
      card.appendChild(add);
    } else {
      const view = document.createElement('a');
      view.className = 'mm-upsell-card__add';
      view.href = product.url;
      view.textContent = 'View';
      card.appendChild(view);
    }

    return card;
  };

  const initUpsell = () => {
    const container = document.querySelector('[data-mm-upsell]:not([data-mm-ready])');
    if (!container) return;
    container.dataset.mmReady = 'true';

    const seedProductId = container.dataset.productId;
    const inCartIds = (container.dataset.inCart || '').split(',').filter(Boolean);
    const cacheKey = `${seedProductId}|${inCartIds.join(',')}`;

    if (!upsellCache.has(cacheKey)) {
      upsellCache.set(
        cacheKey,
        fetchUpsellProducts(seedProductId, inCartIds, container.dataset.fallback, container.dataset.fallbackSort)
      );
    }

    upsellCache.get(cacheKey).then((picks) => {
      // the drawer may have re-rendered while fetching — only touch the live node
      if (!container.isConnected) return;
      if (!picks.length) return;

      const itemsElement = container.querySelector('[data-mm-upsell-items]');
      itemsElement.textContent = '';
      picks.slice(0, 2).forEach((product) => {
        itemsElement.appendChild(buildUpsellCard(product, container.dataset.currency, container.dataset.comingSoon));
      });
      container.hidden = false;
    });
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-mm-upsell-add]');
    if (!addButton || addButton.disabled) return;

    addButton.disabled = true;
    addButton.textContent = 'Adding…';

    fetch(`${window.routes.cart_add_url}.js`, jsonConfig({
      items: [{ id: Number(addButton.dataset.mmUpsellAdd), quantity: 1 }],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname,
    }))
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status !== 200) throw new Error(response.description);
        const drawer = document.querySelector('cart-drawer');
        if (drawer && response.sections) {
          drawer.renderContents(response);
        }
      })
      .catch(() => {
        addButton.disabled = false;
        addButton.textContent = 'Add';
      });
  });

  /* ----------------------------------------------------------------------
     Re-init after every drawer render
     ---------------------------------------------------------------------- */

  const boot = () => {
    initUpsell();

    const drawerRoot = document.getElementById('CartDrawer');
    if (!drawerRoot) return;

    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        initUpsell();
      });
    }).observe(drawerRoot, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
